import type {
  SessionStatus,
  SessionUpdate
} from "shared/utils/live-view/protocol";

/**
 * Where the viewer is in one watch session:
 * idle → requesting (waiting for the `request` ack) → waiting (the applicant
 * hasn't answered) → live ⇄ paused, ending in declined, expired (no answer
 * in time, or the applicant left first), ended or failed (the request itself
 * was refused, e.g. the applicant just left).
 */
export type LiveViewStatus =
  | "idle"
  | "requesting"
  | "waiting"
  | "live"
  | "paused"
  | "declined"
  | "expired"
  | "ended"
  | "failed";

export interface LiveViewSessionState {
  status: LiveViewStatus;
  applicantId: number | null;
  sessionId: string | null;
  endedBy?: "applicant" | "viewer";
  error?: string;
  /**
   * `session` updates that arrived before the `request` ack told us the
   * session id (a race the relay doesn't rule out),
   * latest per session id. Only the acked session's entry is ever applied.
   */
  early?: Record<string, SessionUpdate>;
}

export type LiveViewSessionAction =
  | { type: "watch"; applicantId: number }
  | { type: "requestAcked"; sessionId: string }
  | { type: "requestFailed"; error: string }
  | { type: "sessionUpdate"; update: SessionUpdate }
  | { type: "joinAcked"; sessionId: string; status: SessionStatus }
  | { type: "joinFailed"; sessionId: string; error: string }
  /** The server never said "expired"; the viewer gave up waiting. */
  | { type: "expiredLocally"; sessionId: string }
  /** The socket stayed down too long for the session to survive. */
  | { type: "connectionLost" }
  | { type: "stopped" }
  | { type: "reset" };

export const initialSessionState: LiveViewSessionState = {
  status: "idle",
  applicantId: null,
  sessionId: null
};

const OPEN_STATUSES: readonly LiveViewStatus[] = [
  "requesting",
  "waiting",
  "live",
  "paused"
];

/** A session is open from the request until it's declined, ended or failed. */
export const isSessionOpen = (status: LiveViewStatus) =>
  OPEN_STATUSES.includes(status);

const STATUS_FROM_SERVER: Record<SessionStatus, LiveViewStatus> = {
  requested: "waiting",
  active: "live",
  paused: "paused",
  declined: "declined",
  expired: "expired",
  ended: "ended"
};

const applyUpdate = (
  state: LiveViewSessionState,
  update: SessionUpdate
): LiveViewSessionState => {
  const status = STATUS_FROM_SERVER[update.status];
  if (!status) return state;
  if (status === "ended")
    return { ...state, status, endedBy: update.by, early: undefined };
  return { ...state, status, early: undefined };
};

/** The action is about the session this viewer is watching right now. */
const isCurrent = (state: LiveViewSessionState, sessionId: string) =>
  isSessionOpen(state.status) && state.sessionId === sessionId;

type Handlers = {
  [Type in LiveViewSessionAction["type"]]: (
    state: LiveViewSessionState,
    action: Extract<LiveViewSessionAction, { type: Type }>
  ) => LiveViewSessionState;
};

const handlers: Handlers = {
  watch: (state, action) =>
    isSessionOpen(state.status)
      ? state
      : {
          status: "requesting",
          applicantId: action.applicantId,
          sessionId: null
        },

  requestAcked: (state, action) => {
    if (state.status !== "requesting") return state;
    const waiting: LiveViewSessionState = {
      ...state,
      status: "waiting",
      sessionId: action.sessionId,
      early: undefined
    };
    const early = state.early?.[action.sessionId];
    return early ? applyUpdate(waiting, early) : waiting;
  },

  requestFailed: (state, action) =>
    state.status === "requesting"
      ? { ...state, status: "failed", error: action.error, early: undefined }
      : state,

  sessionUpdate: (state, action) => {
    if (state.status === "requesting")
      return {
        ...state,
        early: { ...state.early, [action.update.session_id]: action.update }
      };
    return isCurrent(state, action.update.session_id)
      ? applyUpdate(state, action.update)
      : state;
  },

  joinAcked: (state, action) =>
    isCurrent(state, action.sessionId)
      ? applyUpdate(state, {
          session_id: action.sessionId,
          status: action.status
        })
      : state,

  joinFailed: (state, action) =>
    isCurrent(state, action.sessionId)
      ? { ...state, status: "ended", error: action.error }
      : state,

  expiredLocally: (state, action) =>
    isCurrent(state, action.sessionId) && state.status === "waiting"
      ? { ...state, status: "expired" }
      : state,

  connectionLost: (state) =>
    isSessionOpen(state.status)
      ? {
          ...state,
          status: "failed",
          error: "connection_lost",
          early: undefined
        }
      : state,

  stopped: (state) =>
    isSessionOpen(state.status)
      ? { ...state, status: "ended", endedBy: "viewer", early: undefined }
      : state,

  reset: () => initialSessionState
};

export const sessionReducer = (
  state: LiveViewSessionState,
  action: LiveViewSessionAction
): LiveViewSessionState => {
  const handler = handlers[action.type] as (
    current: LiveViewSessionState,
    next: LiveViewSessionAction
  ) => LiveViewSessionState;
  return handler(state, action);
};
